import React, { useImperativeHandle, useRef, useState } from "react";
import CalendarEventForm from "../templates/CalendarEventForm";
import { CalendarEventModalLayout } from "./CalendarEventModalLayout";
import { useGetAppointmentQuery } from "app/services/api/calendarApi";
import { CalendarEventDetailView } from "../templates/CalendarEventDetailView";
import { CancelEventModal } from "./CancelEventModal";
import { EventImpl } from "@fullcalendar/core/internal";
import { InitialTimeInterface } from "../hooks/useHandleAppointments";
import { usePermissionCheck } from "app/services/permissions/usePermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { ShiftHandoverTaskClass } from "../../../services/api/types/shiftHandover/classes/ShiftHandoverTaskClass";
import { AppointmentStatusEnums } from "../../../services/api/types/calendar/enums/AppointmentStatusEnums";

export interface CalendarEventModalRef {
  show: (calendarEventId: number | string, event: EventImpl) => void;
  showAppointment: (appointment: ShiftHandoverTaskClass) => void;
  addNew: (initialTimes?: InitialTimeInterface) => void;
}

interface Props {
  onClose?: () => void;
}

const CalendarEventModal = React.forwardRef<CalendarEventModalRef, Props>(
  ({ onClose }, ref) => {
    const cancelEventModalRef =
      useRef<React.ComponentRef<typeof CancelEventModal>>(null);
    const { checkPermission } = usePermissionCheck();

    const [isEditMode, setIsEditMode] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [calendarEventId, setCalendarEventId] = useState<number | null>(null);
    const [calendarEvent, setCalenderEvent] = useState<EventImpl | null>(null);
    const [calendarEventStartDate, setCalendarEventStartDate] =
      useState<Date | null>(null);
    const [calendarEventEndDate, setCalendarEventEndDate] =
      useState<Date | null>(null);
    const [appointmentStatus, setAppointmentStatus] = useState<
      string | undefined
    >(undefined);
    const [initialTimes, setInitialTimes] =
      useState<InitialTimeInterface | null>(null);

    const { data } = useGetAppointmentQuery(
      { id: calendarEventId! },
      { skip: !calendarEventId },
    );

    useImperativeHandle(ref, () => ({
      show: (calendarEventId: string | number, event: EventImpl) => {
        if (!checkPermission(PermissionsEnums.VIEW_CALENDAR_EVENT)) return;
        setCalendarEventId(Number(calendarEventId));
        setCalenderEvent(event);
        setInitialTimes(null);
        setIsEditMode(false);
        setIsVisible(true);
      },
      showAppointment: (appointment: ShiftHandoverTaskClass) => {
        if (!checkPermission(PermissionsEnums.VIEW_CALENDAR_EVENT)) return;
        setCalendarEventId(appointment.id);
        setCalenderEvent(null);
        setCalendarEventStartDate(new Date(appointment.starts_at));
        setCalendarEventEndDate(new Date(appointment.ends_at));
        setAppointmentStatus(appointment.status);
        setInitialTimes(null);
        setIsEditMode(false);
        setIsVisible(true);
      },
      addNew: (initialTimes?: InitialTimeInterface) => {
        if (!checkPermission(PermissionsEnums.CREATE_CALENDAR_EVENT)) return;
        setCalendarEventId(null);
        setCalenderEvent(null);
        setInitialTimes(initialTimes ?? null);
        setIsEditMode(true);
        setIsVisible(true);
      },
    }));

    const handleClose = () => {
      setIsVisible(false);
      if (onClose) {
        onClose();
      }
    };

    const handleEdit = () => {
      if (!checkPermission(PermissionsEnums.UPDATE_CALENDAR_EVENT) || !data)
        return;
      setIsEditMode(true);
    };

    const handleCancel = async () => {
      if (!calendarEvent?.start && calendarEventStartDate) {
        cancelEventModalRef.current?.show(
          calendarEventId!,
          calendarEventStartDate,
        );
      } else {
        cancelEventModalRef.current?.show(
          calendarEventId!,
          calendarEvent?.start,
        );
      }
    };

    const handleCancelSuccess = () => {
      handleClose();
    };

    return (
      <CalendarEventModalLayout
        isVisible={isVisible}
        onClose={handleClose}
        className="w-full max-w-[737px] lg:w-[737px] mx-auto"
      >
        {isEditMode ? (
          <CalendarEventForm
            initialAppointment={calendarEventId ? data : undefined}
            initialTimes={initialTimes ?? undefined}
            onClose={handleClose}
            onCancel={() => setIsEditMode(false)}
          />
        ) : (
          <CalendarEventDetailView
            appointment={data}
            occurrence={{
              start: calendarEvent?.start || calendarEventStartDate,
              end: calendarEvent?.end || calendarEventEndDate,
              isComplete:
                calendarEvent?.extendedProps.isCompleted ||
                appointmentStatus === AppointmentStatusEnums.COMPLETED,
            }}
            appointmentStatus={appointmentStatus}
            onClose={handleClose}
            onEdit={handleEdit}
            onCancel={handleCancel}
          />
        )}

        <CancelEventModal
          ref={cancelEventModalRef}
          onSuccess={handleCancelSuccess}
        />
      </CalendarEventModalLayout>
    );
  },
);

CalendarEventModal.displayName = "CalendarEventModal";
export { CalendarEventModal };
